import React, { useEffect } from 'react';
import { BackHandler, Platform, Pressable, View } from 'react-native';
import Animated, { FadeIn, FadeOut, ZoomIn, ZoomOut } from 'react-native-reanimated';

import { Stack } from '@/components/layout';
import { Loading } from '@/components/ui/loading/loading';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { useUiStore } from '@/stores';
import { colors } from '@/theme/colors';
import { useShallow } from 'zustand/shallow';

type GlobalLoadingOverlayProps = Record<string, never>;

/** Full-screen loading overlay in the app root (avoids RN Modal stacking above system dialogs on Android). */
export function GlobalLoadingOverlay({}: GlobalLoadingOverlayProps) {
  const visible = useUiStore(useShallow(s => s.globalLoadingVisible));
  const { scale } = useScaling();
  const styles = useStyles();

  useEffect(() => {
    if (!visible) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => sub.remove();
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <View style={styles.root} pointerEvents="auto">
      <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.backdrop} />

      <Pressable style={styles.backdropOverlay} onPress={() => {}} />

      <View style={[styles.container, styles.contentContainer]}>
        <Animated.View entering={ZoomIn} exiting={ZoomOut}>
          <Stack
            direction="column"
            minWidth={scale(140)}
            paddingX={scale(16)}
            paddingY={scale(16)}
            borderRadius={scale(12)}
            backgroundColor={colors['Backgrounds/Primary']}>
            <Loading text={STRINGS.loading} indicatorSize="small" />
          </Stack>
        </Animated.View>
      </View>
    </View>
  );
}

const useStyles = createStyles(() => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 999999,
    ...(Platform.OS === 'android' ? { elevation: 999999 } : {}),
  },
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));
